<template>
  <v-autocomplete
    :model-value="selectedBalance"
    :items="balances"
    :item-title="balanceTitle"
    item-value="assignId"
    label="Select buyer balance"
    return-object
    clearable
    :loading="loading"
    :disabled="disabled || loading || buyerId == null"
    :hint="
      buyerId == null
        ? 'Choose an order buyer to see their balances.'
        : 'Available, non-empty balances belonging to the order buyer.'
    "
    persistent-hint
    class="mb-4"
    @update:model-value="selectBalance"
  />
  <v-alert v-if="error" type="error" class="mb-3">
    {{ error }}
    <v-btn variant="text" :disabled="loading" @click="loadBalances"
      >Retry</v-btn
    >
  </v-alert>
  <v-text-field
    v-model="number"
    label="Balance account number"
    :disabled="disabled"
  />
</template>

<script setup lang="ts">
import { computed, ref, shallowRef, watch, onBeforeUnmount } from "vue";
import { useBalanceService } from "../../../cbi-generator/frontend/composables/useBalanceFrontendService";
import type { BalanceStaff } from "../../../cbi-generator/generated/dto/BalanceStaff";
import { errorMessage as requestErrorMessage } from "@/composables/generated/contracts";

const props = defineProps<{ buyerId: number | null; disabled: boolean }>();
const number = defineModel<string>({ required: true });
const balances = shallowRef<BalanceStaff[]>([]);
const loading = ref(false);
const error = ref("");
let version = 0;
const selectedBalance = computed(
  () =>
    balances.value.find(
      (balance) => balance.balanceAccountNumber === number.value,
    ) ?? null,
);
const money = (cents: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    cents / 100,
  );
const balanceTitle = (balance: BalanceStaff) =>
  `${balance.balanceName ? `${balance.balanceName} - ` : ""}${balance.balanceAccountNumber} (${money(balance.spendableAmountCents ?? 0)})`;
function selectBalance(balance: BalanceStaff | null) {
  number.value = balance?.balanceAccountNumber ?? "";
}
async function loadBalances() {
  const current = ++version;
  balances.value = [];
  error.value = "";
  loading.value = false;
  if (props.buyerId == null) return;
  const person = { personId: props.buyerId };
  loading.value = true;
  try {
    const available: BalanceStaff[] = [];
    let page = 1;
    let totalPages = 1;
    do {
      const result = await useBalanceService().balancesStaff({
        person,
        nonZero: true,
        requireLoad: false,
        requirePurchase: false,
        page,
        perPage: 100,
        balanceName: null,
        accountNumber: null,
        balanceType: null,
      });
      if (current !== version) return;
      available.push(
        ...result.items.filter(
          (balance) =>
            (balance.spendableAmountCents ?? 0) > 0 &&
            (balance.balanceExpirationDate == null ||
              balance.balanceExpirationDate.getTime() > Date.now()),
        ),
      );
      totalPages = result.totalPages;
      page++;
    } while (page <= totalPages);
    balances.value = available;
  } catch (failure) {
    if (current === version) error.value = requestErrorMessage(failure);
  } finally {
    if (current === version) loading.value = false;
  }
}
watch(
  () => props.buyerId,
  () => {
    number.value = "";
    void loadBalances();
  },
  { immediate: true },
);
onBeforeUnmount(() => {
  version++;
});
watch(
  () => props.disabled,
  (disabled, previous) => {
    if (previous && !disabled) void loadBalances();
  },
);
</script>
